<template>
  <section id="home" class="hero-gradient-enhanced relative overflow-hidden">
    <div class="absolute inset-x-0 top-0 -z-10 h-[600px] bg-gradient-to-b from-hilab/10 via-white to-white blur-[120px]"></div>
    <ParticleBackground />
    <div class="w-full max-w-none mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 grid gap-12 pb-24 pt-16 md:grid-cols-2 md:items-center md:pb-28 md:pt-20">
      <div>
        <span class="badge">{{ heroBadge }}</span>
        <h1 class="text-hero mt-6">
          {{ heroTitle }}
        </h1>
        <p class="text-hero-subtitle mt-6">
          {{ heroDescription }}
        </p>
        <div class="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          <a :href="primaryCtaUrl" class="primary-button">
            {{ heroPrimaryLabel }}
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 12h14m0 0-5-5m5 5-5 5" />
            </svg>
          </a>
          <!-- <a :href="secondaryCtaUrl" class="secondary-button">
            {{ heroSecondaryLabel }}
          </a> -->
        </div>
        <div class="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-slate-500">
          <div v-for="bullet in heroBullets" :key="bullet" class="flex items-center gap-2">
            <span class="icon-circle">
              <LucideIcon name="check-check" :size="20" class="text-hilab" />
            </span>
            {{ bullet }}
          </div>
        </div>
      </div>
      <div class="relative">
        <div class="absolute -inset-x-10 -top-10 -z-10 h-72 rounded-full bg-hilab/10 blur-3xl"></div>
        <div v-if="primaryImage" class="mb-8 overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-2xl">
          <img
            :src="primaryImage.url"
            :alt="primaryImage.alt_text || primaryImage.title || 'Hero media'"
            class="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
        <div class="card-surface gradient-border relative overflow-hidden px-6 pb-6 pt-8 shadow-2xl">
          <div class="flex items-center gap-4">
            <div class="icon-circle h-14 w-14 bg-hilab text-white shadow-lg">
              <svg class="h-7 w-7" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 5v14m7-7H5" />
              </svg>
            </div>
            <div>
              <p class="text-sm uppercase tracking-[0.2em] text-slate-400">{{ heroInsightTitle }}</p>
              <p class="mt-1 text-2xl font-semibold text-slate-900">{{ heroInsightValue }}</p>
            </div>
          </div>
          <div class="mt-8 grid grid-cols-2 gap-4 text-sm text-slate-500">
            <div v-for="stat in primaryStats" :key="stat.title" class="surface-muted p-4">
              <p class="text-xs uppercase tracking-wide text-slate-400">{{ stat.title }}</p>
              <p class="mt-3 text-2xl font-semibold text-slate-900">{{ stat.value }}</p>
              <p class="mt-2 text-xs text-slate-400">{{ stat.description }}</p>
            </div>
            <div
              v-if="highlightStat"
              class="surface-muted col-span-2 flex flex-col gap-2 rounded-2xl bg-gradient-to-r from-hilab to-hilab-dark px-6 py-4 text-white"
            >
              <p class="text-xs uppercase text-white/70">{{ highlightStat.title }}</p>
              <p class="text-base font-semibold">{{ highlightStat.value }}</p>
              <p class="text-xs text-white/75">{{ highlightStat.description }}</p>
            </div>
          </div>
          <div class="mt-6 flex items-center gap-4 rounded-2xl bg-white/70 p-4">
            <div class="flex -space-x-3">
              <div class="h-10 w-10 rounded-full border-2 border-white bg-[url('https://i.pravatar.cc/100?img=1')] bg-cover"></div>
              <div class="h-10 w-10 rounded-full border-2 border-white bg-[url('https://i.pravatar.cc/100?img=8')] bg-cover"></div>
              <div class="h-10 w-10 rounded-full border-2 border-white bg-[url('https://i.pravatar.cc/100?img=11')] bg-cover"></div>
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-900">{{ heroTrustNote }}</p>
              <p class="text-xs text-slate-400">{{ heroTrustDescription }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';
import ParticleBackground from './ParticleBackground.vue';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  secondaryCtaUrl: {
    type: String,
    required: true,
  },
  demoUrl: {
    type: String,
    default: '/app/accounts/new',
  },
  content: {
    type: Object,
    default: () => ({}),
  },
  images: {
    type: Array,
    default: () => [],
  },
});

const toArray = value => (Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : []);

const hero = computed(() => props.content || {});
const heroImages = computed(() => props.images || []);
const primaryImage = computed(() => heroImages.value[0] || null);

const heroBadge = computed(() => hero.value.badge || 'Nền tảng chăm sóc khách hàng đa kênh + AI');
const heroTitle = computed(
  () =>
    hero.value.title ||
    'HiCHAT đồng hành giúp doanh nghiệp Việt Nam phục vụ khách hàng thông minh hơn trong thời đại 2025'
);
const heroDescription = computed(
  () =>
    hero.value.description ||
    'Gom mọi hội thoại từ email, Facebook, Zalo OA/cá nhân, WhatsApp, SMS, Telegram, Line, Website và API về cùng một nơi; để AI HiCHAT trả lời đúng giọng thương hiệu dựa trên dữ liệu doanh nghiệp; và tự động đồng bộ thông tin sang Google Sheets/Lark, Linear hay hệ thống nội bộ.'
);
const heroPrimaryLabel = computed(() => hero.value.primary_cta_label || 'Dùng thử miễn phí');
const heroSecondaryLabel = computed(() => hero.value.secondary_cta_label || 'Khám phá sản phẩm');
const heroBullets = computed(() => toArray(hero.value.bullets).length ? toArray(hero.value.bullets) : [
  'AI HiCHAT hiểu dữ liệu doanh nghiệp để tóm tắt hội thoại, gợi ý trả lời phù hợp',
  '9+ kênh hội thoại hợp nhất: Email, Facebook, Zalo, WhatsApp, SMS, Telegram, Line, Website, API',
  'Ghi nhận Data Collection và đồng bộ Google Sheets/Lark, Linear chỉ trong vài bước',
]);
const heroInsightTitle = computed(() => hero.value.insight_title || 'Insight thời gian thực');
const heroInsightValue = computed(() => hero.value.insight_value || 'Tăng 68% mức độ hài lòng');
const heroStats = computed(() => toArray(hero.value.stats));
const primaryStats = computed(() => heroStats.value.slice(0, 2));
const highlightStat = computed(() => heroStats.value[2] || null);
const heroTrustNote = computed(() => hero.value.trust_note || 'Đội ngũ bán hàng & CSKH tin dùng HiCHAT mỗi ngày');
const heroTrustDescription = computed(
  () => hero.value.trust_description || 'Dữ liệu hội thoại, Data Collection, SLA được đồng bộ xuyên suốt'
);
</script>
